import type { HTMLAttributes, ReactNode } from 'react';
import { useMemo } from 'react';
import IconChevronUp from '~/vendor/cxy/icons/icons/ChevronUp';
import clsx from 'clsx';

import { Button } from '../Button';

import { useTableScroll } from './hooks/useTableScroll';
import styles from './styles/TableScrollContainer.module.scss';

export interface TableScrollContainerProps extends HTMLAttributes<HTMLDivElement> {
  caption: string;
  cellWidth?: number;
  children: ReactNode;
  maxRows?: number;
}

export const TableScrollContainer = ({
  caption,
  cellWidth = 60,
  children,
  className,
  maxRows,
  ...props
}: TableScrollContainerProps) => {
  const {
    checkScroll,
    currentTransform,
    onScrollClick,
    pinCount,
    rowCount,
    scrollDirection,
    scrollRef,
    shouldShowScrollButtons,
  } = useTableScroll({
    maxRows,
    cellWidth,
  });

  const wrapperClassNames = useMemo(
    () =>
      clsx(
        styles.wrapper,
        {
          [styles.hasScrollShadowTop]: scrollDirection.top,
          [styles.hasScrollShadowBottom]: scrollDirection.bottom,
          [styles.scrollFullWidth]: !scrollDirection.left || !shouldShowScrollButtons,
          [styles.hasVerticalScroll]: scrollDirection.top || scrollDirection.bottom,
          [styles.hasHorizontalScroll]: scrollDirection.left || scrollDirection.right,
        },
        className,
      ),
    [className, scrollDirection, shouldShowScrollButtons],
  );

  const innerWrapperClassNames = useMemo(
    () =>
      clsx(styles.innerWrapper, {
        [styles.hasMaxHeight]: maxRows,
        [styles.hasScrollShadowEnd]: shouldShowScrollButtons && scrollDirection.right,
        [styles.hasScrollShadowStart]: shouldShowScrollButtons && scrollDirection.left,
      }),
    [maxRows, shouldShowScrollButtons, scrollDirection.right, scrollDirection.left],
  );

  const containerStyle = useMemo(
    () => ({
      '--maxRows': rowCount && maxRows && rowCount < maxRows ? rowCount : maxRows,
      '--cellWidth': `${cellWidth}px`,
      '--pinCount': pinCount,
    }),
    [cellWidth, maxRows, pinCount, rowCount],
  );

  const transformStyle = useMemo(
    () => ({
      '--table-horizontal-offset': `translateX(${currentTransform}px)`,
    }),
    [currentTransform],
  );

  return (
    <div className={wrapperClassNames} style={containerStyle} {...props}>
      <div className={styles.captionWrapper}>
        <h2 className={styles.caption}>{caption}</h2>
        {shouldShowScrollButtons && (
          <div className={styles.buttonWrapper} data-testid="scroll-controls">
            <Button
              aria-label="scroll table left"
              className={styles.button}
              disabled={!scrollDirection.left}
              onClick={() => onScrollClick('left')}
              variant="ghost"
            >
              <IconChevronUp size="xsm" />
            </Button>
            <Button
              aria-label="scroll table right"
              className={styles.button}
              disabled={!scrollDirection.right}
              onClick={() => onScrollClick('right')}
              variant="ghost"
            >
              <IconChevronUp size="xsm" />
            </Button>
          </div>
        )}
      </div>
      <div
        className={innerWrapperClassNames}
        onScroll={() => checkScroll(!!maxRows)}
        ref={scrollRef}
        style={transformStyle}
      >
        {children}
      </div>
    </div>
  );
};
